import { type ActionCreator, on, type ReducerTypes } from '@ngrx/store';
import { produce } from 'immer';
import { isEmpty, pick } from 'lodash-es';

import { isEqualNoUndefined } from '@blaze-shared/shared-util';

import {
  clearHistory,
  redo,
  requestHistoryFail,
  setHistoryRestoring,
  takeHistorySnapshot,
  undo,
} from './editor-history.actions';
import { type HistoryEngineState, type LayoutSnapshot } from './editor-history.state';

const HISTORY_SNAPSHOT_SLICES = ['ids', 'entities', 'connections'];
const HISTORY_MAX_SNAPSHOTS = 5;

export const historyReducerTypes: readonly ReducerTypes<HistoryEngineState, ActionCreator[]>[] = [
  on(requestHistoryFail, (state) =>
    produce(state, (draft) => {
      if (!draft.history) {
        return;
      }
      draft.history.isRestoring = false;
    }),
  ),
  on(setHistoryRestoring, (state, { isRestoring }) => {
    return produce(state, (draft) => {
      if (!draft.history) {
        return;
      }
      draft.history.isRestoring = isRestoring;
    });
  }),
  on(undo, (state, { patch }) => {
    return produce(state, (draft) => {
      if (!draft.history || draft.history.past.length === 0) {
        return;
      }
      const previousSnapshot = draft.history.past.pop();
      if (!previousSnapshot) {
        return;
      }
      const currentStateSnapshot = pick(draft, HISTORY_SNAPSHOT_SLICES);
      draft.history.future.unshift(currentStateSnapshot);
      Object.assign(draft, previousSnapshot, patch);
      draft.history.snapshot = previousSnapshot;
      draft.history.isRestoring = false;
    });
  }),
  on(redo, (state, { patch }) =>
    produce(state, (draft) => {
      if (!draft.history || draft.history.future.length === 0) {
        return;
      }
      const futureSnapshot = draft.history.future.shift();
      if (!futureSnapshot) {
        return;
      }
      draft.history.past.push(pick(draft, HISTORY_SNAPSHOT_SLICES));
      Object.assign(draft, futureSnapshot, patch);
      draft.history.snapshot = futureSnapshot;
      draft.history.isRestoring = false;
    }),
  ),
  on(clearHistory, (state) =>
    produce(state, (draft) => {
      if (!draft.history) {
        return;
      }
      draft.history.past = [];
      draft.history.future = [];
      draft.history.snapshot = undefined;
    }),
  ),
  on(takeHistorySnapshot, (state) => {
    return produce(state, (draft) => {
      if (!draft.history) {
        return;
      }
      const nextSnapshot = pick(draft, HISTORY_SNAPSHOT_SLICES);
      const lastSnapshot = draft.history.snapshot;

      if (isEqualNoUndefined(nextSnapshot, lastSnapshot)) {
        return;
      }

      if (lastSnapshot && !isEmptySnapshot(lastSnapshot)) {
        draft.history.past.push(lastSnapshot);
      }
      if (draft.history.past.length > HISTORY_MAX_SNAPSHOTS) {
        const drop = draft.history.past.length - HISTORY_MAX_SNAPSHOTS;
        draft.history.past.splice(0, drop);
      }

      draft.history.snapshot = nextSnapshot;
      draft.history.future = [];
    });
  }),
];

const isEmptySnapshot = (snapshot: LayoutSnapshot): boolean =>
  Object.values(snapshot).every((value) => isEmpty(value));
